import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Modal } from 'react-bootstrap';
import emailjs from '@emailjs/browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';

interface ContactInfo {
  email: string;
  phone: string;
  address: string;
  mapUrl?: string;
}

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    message: '',
  });
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    email: '',
    phone: '',
    address: '',
    mapUrl: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false); // Modal visibility state

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const res = await fetch(process.env.REACT_APP_API_URL + '/api/contact-info', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await res.json();
        if (res.ok) {
          setContactInfo({
            email: data.email,
            phone: data.phone,
            address: data.address,
            mapUrl: data.mapUrl,
          });
        } else {
          setError(data.message || 'Error fetching contact information.');
        }
      } catch (err) {
        console.error('Error fetching contact info:', err);
        setError('Error fetching contact information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  // Show modal and auto-close after 3 seconds
  useEffect(() => {
    if (successMessage || errorMessage) {
      setShowModal(true);
      const timer = setTimeout(() => {
        setShowModal(false);
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 3000); // Modal closes after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Send email using EmailJS
      const emailResponse = await emailjs.send(
        'service_3tx8jli', // Your EmailJS service ID
        'template_2nq3cmj', // Your EmailJS template ID
        {
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          message: formData.message,
        },
        '2znub3nsdivNcJHnz' // Your EmailJS user ID
      );

      console.log('Email sent successfully:', emailResponse.status, emailResponse.text);
      setSuccessMessage('Thank you for reaching out! We will get back to you soon.');
      setFormData({ name: '', email: '', mobile: '', message: '' });
    } catch (err) {
      console.error('Error sending email:', err);
      setErrorMessage('Error sending message. Please try again.');
    }
  };

  return (
    <section className="contact-section py-5 bg-light">
      <Container>
        {/* Contact Form and Details */}
        <h2 className="text-center mb-5 contact-title">Contact Us</h2>
        <Row>
          {/* Left Side Content */}
          <Col md={6} className="mb-4">
            <div className="contact-info">
              <h3 className="contact-subtitle">Get in Touch</h3>
              <p className="contact-text">
                We’d love to hear from you! Whether you have questions, want to collaborate, or wish to support our mission, feel free to get in touch.
              </p>
              {loading ? (
                <p>Loading contact information...</p>
              ) : error ? (
                <p className="text-danger">{error}</p>
              ) : (
                <>
                  <p className="contact-detail">
                    <i className="fas fa-envelope me-2"></i> Email: {contactInfo.email}
                  </p>
                  <p className="contact-detail">
                    <i className="fas fa-phone me-2"></i> Phone: {contactInfo.phone}
                  </p>
                  <p className="contact-detail">
                    {/* <i className="fas fa-map-marker-alt me-2"></i> Address: {contactInfo.address} */}
                  </p>
                </>
              )}
            </div>
          </Col>

          {/* Right Side Form */}
          <Col md={6} className="mb-4">
            <div className="contact-form-box">
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="formName">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formMobile">
                  <Form.Label>Mobile Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter your mobile number"
                    pattern="[0-9]{10}"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formMessage">
                  <Form.Label>Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your message"
                    required
                  />
                </Form.Group>

                <Button variant="primary" type="submit" className="contact-btn">
                  Send Message
                </Button>
              </Form>
            </div>
          </Col>
        </Row>

        {/* Location Map Section */}
        {/* <Row className="mt-5">
          <Col>
            <h3 className="text-center mb-4 location-title">Our Location</h3>
            <div className="location-map">
              {loading ? (
                <p>Loading map...</p>
              ) : error || !contactInfo.mapUrl ? (
                <p className="text-danger">Map not available.</p>
              ) : (
                <iframe
                  src={contactInfo.mapUrl}
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  title="NGO Location Map"
                ></iframe>
              )}
            </div>
          </Col>
        </Row> */}

        {/* Success/Error Modal */}
        <Modal show={showModal} centered>
          <Modal.Body className="text-center">
            {successMessage ? (
              <div className="d-flex flex-column align-items-center">
                <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
                <h4 className="text-success mb-2">Success!</h4>
                <p>{successMessage}</p>
              </div>
            ) : (
              <div className="d-flex flex-column align-items-center">
                <FontAwesomeIcon icon={faTimesCircle} size="3x" className="text-danger mb-3" />
                <h4 className="text-danger mb-2">Error!</h4>
                <p>{errorMessage}</p>
              </div>
            )}
          </Modal.Body>
        </Modal>
      </Container>
    </section>
  );
};

export default ContactSection;